import { useEffect, useMemo, useState } from 'react';
import {
  ALWAYS_AVAILABLE,
  DESTINATION_STORAGE_KEY,
  LABEL_DESTINATIONS,
  parseDestinationId,
  type DestinationAvailability,
  type LabelDestinationId,
} from './destinations.js';
import type { DymoSnapshot } from './dymo-browser.js';
import { PERMISSION_WAIT_MESSAGE, unavailableReason } from './dymo-status.js';
import { useDymoStatus } from './useDymoStatus.js';

/**
 * This browser's label destination, read from and written to localStorage the
 * way the theme is (components/ThemeToggle.tsx): guarded, because a private
 * window throws on access, and announced, because the Labeling page and the
 * settings page can both be showing it.
 */

const CHANGE_EVENT = 'seller:label-destination-change';

/** What a destination that must be checked shows until the check answers. */
const CHECKING: DestinationAvailability = {
  available: false,
  reason: 'Checking for DYMO Connect on this computer…',
};

function readStored(): LabelDestinationId {
  try {
    return parseDestinationId(localStorage.getItem(DESTINATION_STORAGE_KEY));
  } catch {
    return parseDestinationId(null);
  }
}

export function useLabelDestination(): [LabelDestinationId, (id: LabelDestinationId) => void] {
  const [id, setId] = useState<LabelDestinationId>(readStored);

  useEffect(() => {
    function onChange(event: Event) {
      setId((event as CustomEvent<LabelDestinationId>).detail);
    }
    // Another tab of this browser changing it is the same setting changing.
    function onStorage(event: StorageEvent) {
      if (event.key === DESTINATION_STORAGE_KEY) setId(parseDestinationId(event.newValue));
    }
    window.addEventListener(CHANGE_EVENT, onChange);
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener(CHANGE_EVENT, onChange);
      window.removeEventListener('storage', onStorage);
    };
  }, []);

  function update(next: LabelDestinationId) {
    setId(next);
    try {
      localStorage.setItem(DESTINATION_STORAGE_KEY, next);
    } catch {
      // The choice simply will not persist; this visit still honors it.
    }
    window.dispatchEvent(new CustomEvent<LabelDestinationId>(CHANGE_EVENT, { detail: next }));
  }

  return [id, update];
}

/**
 * Whether the DYMO printer can be used, from the shared status: not until the
 * first check has answered, and afterwards exactly when a connected LabelWriter
 * was found. While a later check runs, the last answer stands. A first check
 * held up by the browser's permission question says that instead of "checking".
 */
export function dymoAvailability(snapshot: DymoSnapshot): DestinationAvailability {
  if (snapshot.status === null) {
    return snapshot.waitingForPermission
      ? { available: false, reason: PERMISSION_WAIT_MESSAGE }
      : CHECKING;
  }
  const reason = unavailableReason(snapshot.status);
  return { available: reason === null, reason };
}

/**
 * Whether each destination can be used on this computer right now. A PDF always
 * can. The DYMO printer is looked for when the component mounts (this hook is
 * the settings page's picker) and is NOT available until the check says so, so
 * it can never be chosen in the moment before the answer arrives; "Check again"
 * updates it through the same shared status.
 */
export function useDestinationAvailability(): Record<LabelDestinationId, DestinationAvailability> {
  const dymo = useDymoStatus(true);
  return useMemo(
    () =>
      Object.fromEntries(
        LABEL_DESTINATIONS.map((d) => [
          d.id,
          d.kind === 'dymo' ? dymoAvailability(dymo) : ALWAYS_AVAILABLE,
        ]),
      ) as Record<LabelDestinationId, DestinationAvailability>,
    [dymo],
  );
}
